import { getSupabaseClient } from "../providers/supabase/supabase";

const FEEDBACK_API_PATH = "/api/echo-feedback";
const FEEDBACK_FRAME_API_PATH = "/api/echo-feedback-frame";

export class EchoFeedbackApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(message: string, status: number, code?: string | null) {
    super(message);
    this.name = "EchoFeedbackApiError";
    this.status = status;
    this.code = code ?? null;
  }
}

export async function feedbackApiRequest<T>(
  path = FEEDBACK_API_PATH,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(init.headers ?? {}),
      ...(await requiredFeedbackAuthHeader()),
    },
  });
  const body = await response.text();
  const parsed = parseJson(body);
  if (!response.ok) {
    const record = isRecord(parsed) ? parsed : null;
    throw new EchoFeedbackApiError(
      stringValue(record?.message) ||
        stringValue(record?.error) ||
        body ||
        `Feedback request failed (${response.status})`,
      response.status,
      stringValue(record?.code),
    );
  }
  return parsed as T;
}

export async function fetchFeedbackFrame(
  reportId: string,
  signal?: AbortSignal,
): Promise<Blob> {
  const url = new URL(FEEDBACK_FRAME_API_PATH, window.location.origin);
  url.searchParams.set("id", reportId);
  const response = await fetch(`${url.pathname}${url.search}`, {
    headers: await requiredFeedbackAuthHeader(),
    cache: "no-store",
    signal,
  });
  if (!response.ok) {
    const body = await response.text();
    throw new EchoFeedbackApiError(
      body || `Feedback frame request failed (${response.status})`,
      response.status,
    );
  }
  return response.blob();
}

async function requiredFeedbackAuthHeader(): Promise<
  Record<"Authorization", string>
> {
  let token: string | undefined;
  try {
    const { data } = await getSupabaseClient().auth.getSession();
    token = data.session?.access_token;
  } catch {
    token = undefined;
  }
  if (!token) {
    throw new EchoFeedbackApiError(
      "Your CRM session expired. Sign in again before using feedback.",
      401,
      "feedback_session_missing",
    );
  }
  return { Authorization: `Bearer ${token}` };
}

function parseJson(body: string): unknown {
  if (!body) return null;
  try {
    return JSON.parse(body);
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringValue(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}
